import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { PermissionsImportStructuredData, PermissionsImportValidatedData, Role } from "./PermissionsImport.d";
import { assignImportedRoles, fetchRoles, importCsvFile, processFileInput } from "./PermissionsImport.function";
import "./PermissionsImport.scss";

export const PermissionsImport = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<Array<PermissionsImportStructuredData>>([]);
  const [validatedData, setValidatedData] = useState<Array<PermissionsImportValidatedData>>([]);
  const [validating, setValidating] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    fetchRoles(setRoles, language);
  }, [language]);

  const _uploadCsvFile = useCallback(
    (file: File) => {
      if (showAlert && popAlert) {
        importCsvFile(file, language, setCsvData, setValidatedData, setValidating, showAlert, popAlert);
      }
    },
    [language, showAlert, popAlert]
  );

  const _displayScope = (scope: number[] | string): string => {
    if (typeof scope === "string") return scope;
    return scope?.length ? scope.join(", ") : "—";
  };

  const columns = [
    {
      name: translate(language, "GLOBAL.EMAIL"),
      key: "email",
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.ROLE"),
      key: "role_id",
      render: (line: PermissionsImportValidatedData & PermissionsImportStructuredData) => {
        const selectedRole = roles.find((role) => role.id === line.role_id);
        return (
          <div id="role-import-dropdown">
            <MultiSelectDropdown
              items={roles.map((role) => ({ id: role.id, name: role.name }))}
              label={selectedRole?.name ?? translate(language, "PAGES.PERMISSIONS_IMPORT.SELECT_ROLE")}
              language={language}
              displayItem={(item) => <span>{item.name}</span>}
              badgeItemProperty="name"
              isSingleSelect
              defaultSelectedItems={selectedRole ? [{ id: selectedRole.id, name: selectedRole.name }] : []}
              onConfirm={([selected]) => {
                setValidatedData((prev) =>
                  prev.map((row) => (row.rowIndex === line.rowIndex ? { ...row, role_id: selected?.id ?? null } : row))
                );
                setCsvData((prev) =>
                  prev.map((row) => (row.rowIndex === line.rowIndex ? { ...row, role_id: selected?.id ?? null } : row))
                );
              }}
              toggleClass="min-w-150px s-bold"
              isSingleSelectRemovable
            />
          </div>
        );
      },
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.USERS_SCOPE"),
      key: "users_ids_scope",
      render: (line: PermissionsImportStructuredData) => <span>{_displayScope(line.users_ids_scope)}</span>,
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.ORGANIZERS_SCOPE"),
      key: "organizers_ids_scope",
      render: (line: PermissionsImportStructuredData) => <span>{_displayScope(line.organizers_ids_scope)}</span>,
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.COHORTS_SCOPE"),
      key: "cohorts_ids_scope",
      render: (line: PermissionsImportStructuredData) => <span>{_displayScope(line.cohorts_ids_scope)}</span>,
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.ANIMATORS_SCOPE"),
      key: "animators_ids_scope",
      render: (line: PermissionsImportStructuredData) => <span>{_displayScope(line.animators_ids_scope)}</span>,
    },
    {
      name: translate(language, "PAGES.PERMISSIONS_IMPORT.TABLE_HEADER.PROJECTS_SCOPE"),
      key: "projects_ids_scope",
      render: (line: PermissionsImportStructuredData) => <span>{_displayScope(line.projects_ids_scope)}</span>,
    },
  ];

  return (
    <div className="p-3">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <h2 className="mb-0">{translate(language, "PAGES.PERMISSIONS_IMPORT.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.PERMISSIONS_IMPORT.NEW_CSV_IMPORT")}
                  variant="light"
                  clickFunction={() => document.getElementById("file-input")?.click()}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.PERMISSIONS_IMPORT.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/permissions_assignment_template.csv";
                  link.download = "permissions_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "GLOBAL.CONFIRM")}
                variant="primary"
                loading={submitting || validating}
                disabled={submitting || validating || validatedData.length === 0}
                clickFunction={() => {
                  if (showAlert && popAlert) {
                    assignImportedRoles(
                      validatedData,
                      language,
                      setSubmitting,
                      () => {
                        setCsvData([]);
                        setValidatedData([]);
                      },
                      showAlert,
                      popAlert
                    );
                  }
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      {csvData.length === 0 ? (
        <div className="d-flex flex-column gap-2 clickable">
          <DstTable data={csvData} columns={columns} itemsPerPage={20} />
          <div
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              if (file) _uploadCsvFile(file);
            }}
          >
            <div className="text-center s-regular d-flex align-items-center">
              {translate(language, "PAGES.PERMISSIONS_IMPORT.DROP_FILE")}
              <DstButton
                value={translate(language, "PAGES.PERMISSIONS_IMPORT.LOAD_LOCAL_FILE")}
                variant="link"
                btnClass="ps-1"
                clickFunction={() => document.getElementById("file-input")?.click()}
              />
            </div>
          </div>
        </div>
      ) : (
        <DstTable
          data={validatedData.map((vData, index) => ({
            ...vData,
            rowIndex: index,
          }))}
          columns={columns}
          itemsPerPage={20}
          classTable="xs-bold"
        />
      )}
      <input
        type="file"
        id="file-input"
        className="d-none"
        onChange={(event) => processFileInput(event, _uploadCsvFile)}
      />
    </div>
  );
};

export default PermissionsImport;
